/* Elects one leader among the open xPT tabs, so N tabs make one set of export polls and one download.
 *
 * - The leader holds a localStorage heartbeat (`{tabId, expiresAt}`) that it renews every 5 s and
 *   that expires after 15 s, so a crashed tab is replaced.
 * - A fresh claim is confirmed by a jittered re-read (last write wins), so two tabs that claim the
 *   same free slot at the same moment resolve to one leader instead of both leading until the next
 *   heartbeat.
 * - Only a visible tab leads: a tab that becomes hidden steps down, and a visible tab takes over.
 * - A BroadcastChannel tells the other tabs promptly when the leader steps down and when an export
 *   changed. Without BroadcastChannel the heartbeat alone still elects a leader.
 */
import { v4 as uuidv4 } from 'uuid';
import {
  ACTUALS_EXPORT_CHANNEL_NAME,
  ACTUALS_EXPORT_CLAIM_CONFIRM_JITTER_MS,
  ACTUALS_EXPORT_CLAIM_CONFIRM_MIN_MS,
  ACTUALS_EXPORT_HEARTBEAT_INTERVAL_MS,
  ACTUALS_EXPORT_LEADER_TTL_MS
} from 'src/constants/actuals-export-constants';
import { eLocalStorageKeys } from 'src/constants/generic-constants';

interface LeaderRecord {
  tabId: string;
  expiresAt: number;
}

type CoordinatorMessage = { type: 'leader-released' } | { type: 'export-updated' };

export interface ActualsExportTabCoordinatorOptions {
  onLeadershipChange: (isLeader: boolean) => void;
  /** Another tab reported that the export changed. */
  onExportUpdated: () => void;
}

const isCoordinatorMessage = (value: unknown): value is CoordinatorMessage =>
  typeof value === 'object' &&
  value !== null &&
  ((value as { type?: unknown }).type === 'leader-released' || (value as { type?: unknown }).type === 'export-updated');

const readLeader = (): LeaderRecord | null => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(eLocalStorageKeys.ActualsExportLeader) ?? 'null');
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    const { tabId, expiresAt } = parsed as Record<string, unknown>;
    return typeof tabId === 'string' && typeof expiresAt === 'number' ? { tabId, expiresAt } : null;
  } catch {
    return null;
  }
};

const createChannel = (): BroadcastChannel | null => {
  try {
    return typeof BroadcastChannel === 'function' ? new BroadcastChannel(ACTUALS_EXPORT_CHANNEL_NAME) : null;
  } catch {
    return null;
  }
};

export class ActualsExportTabCoordinator {
  readonly tabId: string = uuidv4();
  private readonly options: ActualsExportTabCoordinatorOptions;
  private channel: BroadcastChannel | null = null;
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private confirmTimer: ReturnType<typeof setTimeout> | null = null;
  private leader = false;
  private visible = false;
  private started = false;

  constructor(options: ActualsExportTabCoordinatorOptions) {
    this.options = options;
  }

  start(isVisible: boolean): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.visible = isVisible;
    this.channel = createChannel();
    if (this.channel) {
      this.channel.onmessage = (event: MessageEvent<unknown>) => this.handleMessage(event.data);
    }
    window.addEventListener('pagehide', this.handlePageHide);
    this.heartbeat = setInterval(() => this.check(), ACTUALS_EXPORT_HEARTBEAT_INTERVAL_MS);
    this.check();
  }

  stop(): void {
    if (!this.started) {
      return;
    }
    this.started = false;
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
    window.removeEventListener('pagehide', this.handlePageHide);
    this.release();
    this.channel?.close();
    this.channel = null;
  }

  /** A hidden tab steps down; a visible tab tries to lead. */
  setVisible(isVisible: boolean): void {
    this.visible = isVisible;
    if (this.started) {
      this.check();
    }
  }

  isLeader(): boolean {
    return this.leader;
  }

  /** Tells the other tabs that the export changed, so they re-read it. */
  broadcastExportUpdated(): void {
    this.post({ type: 'export-updated' });
  }

  private readonly handlePageHide = (): void => {
    this.release();
  };

  private handleMessage(data: unknown): void {
    if (!isCoordinatorMessage(data)) {
      return;
    }
    if (data.type === 'leader-released') {
      this.check();
    } else {
      this.options.onExportUpdated();
    }
  }

  /** Claims a free or expired slot (confirmed by a jittered re-read), renews an owned one. */
  private check(): void {
    if (!this.visible) {
      this.release();
      return;
    }
    const now = Date.now();
    const current = readLeader();
    if (current !== null && current.expiresAt > now && current.tabId !== this.tabId) {
      this.clearClaimConfirm();
      this.setLeader(false);
      return;
    }
    const isRenewal = current?.tabId === this.tabId;
    try {
      localStorage.setItem(
        eLocalStorageKeys.ActualsExportLeader,
        JSON.stringify({ tabId: this.tabId, expiresAt: now + ACTUALS_EXPORT_LEADER_TTL_MS })
      );
    } catch {
      // Storage is unavailable (quota or privacy mode): there is no way to coordinate, so lead.
      this.setLeader(true);
      return;
    }
    if (isRenewal) {
      // Renewing a slot this tab already holds cannot race another tab: the record was ours and
      // unexpired, so no other tab could claim it.
      this.setLeader(readLeader()?.tabId === this.tabId);
      return;
    }
    // A fresh claim can race another tab claiming the same free slot: a synchronous read-back
    // only proves this tab's own write, not that it won. Stay a follower until a jittered
    // re-read confirms this tab's write survived (localStorage is last-write-wins).
    this.scheduleClaimConfirm();
  }

  private scheduleClaimConfirm(): void {
    if (this.confirmTimer !== null) {
      return;
    }
    const delay = ACTUALS_EXPORT_CLAIM_CONFIRM_MIN_MS + Math.random() * ACTUALS_EXPORT_CLAIM_CONFIRM_JITTER_MS;
    this.confirmTimer = setTimeout(() => {
      this.confirmTimer = null;
      if (!this.started || !this.visible) {
        return;
      }
      this.setLeader(readLeader()?.tabId === this.tabId);
    }, delay);
  }

  private clearClaimConfirm(): void {
    if (this.confirmTimer !== null) {
      clearTimeout(this.confirmTimer);
      this.confirmTimer = null;
    }
  }

  private release(): void {
    this.clearClaimConfirm();
    // An unconfirmed claim is cleaned up too, so a closing tab never leaves a record that blocks
    // the others until it expires.
    let ownedRecord = false;
    try {
      ownedRecord = readLeader()?.tabId === this.tabId;
      if (ownedRecord) {
        localStorage.removeItem(eLocalStorageKeys.ActualsExportLeader);
      }
    } catch {
      // Nothing to clean up when storage is unavailable.
    }
    const wasLeader = this.leader;
    this.setLeader(false);
    if (wasLeader || ownedRecord) {
      this.post({ type: 'leader-released' });
    }
  }

  private setLeader(isLeader: boolean): void {
    if (isLeader !== this.leader) {
      this.leader = isLeader;
      this.options.onLeadershipChange(isLeader);
    }
  }

  private post(message: CoordinatorMessage): void {
    try {
      this.channel?.postMessage(message);
    } catch {
      // A closed channel just means no other tab is told.
    }
  }
}
